import type { CSSProperties } from 'react';
import { TABLET_MIN, useMediaQuery } from './useBreakpoint.js';

export interface EnvBannerProps {
  /** Render nothing when false. Apps pass `APP_ENV !== 'prod'`. */
  show: boolean;
  /** Ribbon text. Defaults to `TEST ENVIRONMENT`. */
  label?: string;
  /**
   * On a phone, render as a strip in the page flow instead of the corner badge.
   * For pages whose header sits in that corner (the admin app); leave off for
   * full-height layouts, which the extra strip would push past the fold.
   */
  inlineOnPhone?: boolean;
}

/**
 * Marks non-production deployments (staging / local). Purely decorative — it
 * never intercepts clicks (`pointerEvents: none`). Hidden in production by
 * passing `show={false}`.
 *
 * By default a badge pinned to the viewport's top-right corner, above app chrome
 * so it stays visible on every screen. With `inlineOnPhone`, a phone gets a thin
 * strip at the top of the page instead: in the admin app that corner is the page
 * header, and the badge covered its text and buttons.
 */
export function EnvBanner({
  show,
  label = 'TEST ENVIRONMENT',
  inlineOnPhone = false,
}: EnvBannerProps) {
  const phone = !useMediaQuery(`(min-width: ${TABLET_MIN}px)`, true) && inlineOnPhone;
  if (!show) return null;

  const strip: CSSProperties = {
    padding: '4px 12px',
    background: '#E07A3F',
    color: '#FFFFFF',
    fontFamily: 'var(--font-sans, system-ui, sans-serif)',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    textAlign: 'center',
    pointerEvents: 'none',
    userSelect: 'none',
  };

  const style: CSSProperties = {
    position: 'fixed',
    top: 12,
    right: 12,
    padding: '6px 12px',
    background: '#E07A3F',
    color: '#FFFFFF',
    fontFamily: 'var(--font-sans, system-ui, sans-serif)',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    borderRadius: 6,
    boxShadow: '0 2px 10px rgba(0,0,0,0.18)',
    pointerEvents: 'none',
    userSelect: 'none',
    zIndex: 2147483647,
  };

  return (
    <div role="status" aria-label={label} className="gc-env-banner" style={phone ? strip : style}>
      {label}
    </div>
  );
}
